import {pipe} from 'rxjs';
import {AuthGuard, customClaims, redirectLoggedInTo, redirectUnauthorizedTo} from '@angular/fire/auth-guard';
import {map} from 'rxjs/operators';
import {Routes} from '@angular/router';
import {LayoutComponent} from './components/layout/layout.component';
import {AccountsComponent} from './components/accounts/accounts.component';
import {AccountFormComponent} from './components/accounts/account-form.component';
import {CanDeactivateGuard} from './guards/can-deactive.guard';
import {AccountUsersComponent} from './components/account-users/account-users.component';
import {UsersComponent} from './components/users/users.component';
import {UsersFormComponent} from './components/users/users.form.component';
import {FacilitiesComponent} from './components/facilities/facilities.component';
import {LocationsComponent} from './components/locations/locations.component';
import {ItemsComponent} from './components/items/items.component';
import {FacilityItemsComponent} from './components/facility-items/facility-items.component';
import {FacilityFormComponent} from './components/facilities/facility-form.component';
import {LocationFormComponent} from './components/locations/location-form.component';
import {ItemUploadComponent} from './components/item-upload/item-upload.component';
import {ItemSearchComponent} from './components/item-search/item-search.component';
import {ItemDetailsComponent} from './components/item-details/item-details.component';
import {LoginLayoutComponent} from './components/login-layout/login-layout.component';
import {LoginComponent} from './components/login/login.component';
import {ForgotPasswordFormComponent} from './components/users/forgot-password-form.component';

const hasTrueCustomClaim = (claim: string) => pipe(customClaims, map(claims => claims.hasOwnProperty(claim) && claims[claim]));
const adminOnly = () => hasTrueCustomClaim('admin');
const atLeastOperator = () => hasTrueCustomClaim('operator');
const redirectUnauthorizedToLogin = () => redirectUnauthorizedTo(['login']);
const redirectLoggedInToAccounts = () => redirectLoggedInTo(['/']);

export const routes: Routes = [
    {
        path: '',
        component: LayoutComponent,
        canActivate: [AuthGuard],
        data: {authGuardPipe: redirectUnauthorizedToLogin},
        children: [
            {
                path: '',
                component: AccountsComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator},
            },
            {
                path: 'accounts/add',
                component: AccountFormComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: adminOnly},
                canDeactivate: [CanDeactivateGuard]
            },
            {
                path: 'accounts/edit/:accountId',
                component: AccountFormComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: adminOnly},
                canDeactivate: [CanDeactivateGuard]
            },
            {
                path: 'account-users/:accountId',
                component: AccountUsersComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator}
            },
            {
                path: 'users',
                component: UsersComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator}
            },
            {
                path: 'users/add',
                component: UsersFormComponent,
                canDeactivate: [CanDeactivateGuard],
                canActivate: [AuthGuard],
                data: {authGuardPipe: adminOnly},
            },
            {
                path: 'users/edit/:userId',
                component: UsersFormComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator},
                canDeactivate: [CanDeactivateGuard],
            },
            {
                path: 'users/account/:accountId/edit/:userId', component: UsersFormComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator},
                canDeactivate: [CanDeactivateGuard],
            },
            {
                path: 'facilities/:accountId',
                component: FacilitiesComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator}
            },
            {
                path: 'facilities/:facilityId/locations',
                component: LocationsComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator}
            },
            {
                path: 'facilities/:facilityId/locations/:locationId/items',
                component: ItemsComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator}
            },
            {
                path: 'facilities/:facilityId/items',
                component: FacilityItemsComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator}
            },
            {
                path: 'facilities/:accountId/add',
                component: FacilityFormComponent,
                canDeactivate: [CanDeactivateGuard],
                canActivate: [AuthGuard],
                data: {authGuardPipe: adminOnly},
            },
            {
                path: 'facilities/edit/:facilityId',
                component: FacilityFormComponent,
                canDeactivate: [CanDeactivateGuard],
                canActivate: [AuthGuard],
                data: {authGuardPipe: adminOnly},
            },
            {
                path: 'facilities/:facilityId/locations/add', component: LocationFormComponent,
                canDeactivate: [CanDeactivateGuard],
                canActivate: [AuthGuard],
                data: {authGuardPipe: adminOnly},
            },
            {
                path: 'facilities/:facilityId/locations/edit/:locationId',
                component: LocationFormComponent,
                canDeactivate: [CanDeactivateGuard],
                canActivate: [AuthGuard],
                data: {authGuardPipe: adminOnly},
            },
            {
                path: 'items/csv-upload',
                component: ItemUploadComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator}
            },
            {
                path: 'items/search',
                component: ItemSearchComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator}
            },
            {
                path: 'item/:itemId',
                component: ItemDetailsComponent,
                canActivate: [AuthGuard],
                data: {authGuardPipe: atLeastOperator}
            },
        ]
    },
    {
        path: '',
        component: LoginLayoutComponent,
        canActivate: [AuthGuard], data: {authGuardPipe: redirectLoggedInToAccounts},
        children: [
            {path: '', pathMatch: 'full', redirectTo: '/login'},
            {path: 'login', component: LoginComponent},
            {path: 'forgot-password', component: ForgotPasswordFormComponent}
        ]
    },
    {path: '**', redirectTo: '/'}
];
